import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Notification } from '../models/notification.models';
import { SignalRService } from './signalr.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements OnDestroy {
  private http = inject(HttpClient);
  private signalRService = inject(SignalRService);

  private readonly apiUrl = `${environment.apiUrl}/api/notifications`;

  private readonly _notifications$ = new BehaviorSubject<Notification[]>([]);
  readonly notifications$ = this._notifications$.asObservable();

  private readonly _unreadCount$ = new BehaviorSubject<number>(0);
  readonly unreadCount$ = this._unreadCount$.asObservable();

  private readonly _newNotification$ = new Subject<Notification>();
  readonly newNotification$ = this._newNotification$.asObservable();

  async initializeSignalR(): Promise<void> {
    await this.signalRService.startConnection();
    this.signalRService.on<Notification>('ReceiveNotification', (notification) => {
      const current = this._notifications$.getValue();
      this._notifications$.next([notification, ...current]);
      this._unreadCount$.next(this._unreadCount$.getValue() + 1);
      this._newNotification$.next(notification);
    });
  }

  getNotifications(): Observable<Notification[]> {
    return this.http.get<Notification[]>(this.apiUrl).pipe(
      tap((notifications) => {
        this._notifications$.next(notifications);
        this._unreadCount$.next(notifications.filter((n) => !n.isRead).length);
      }),
    );
  }

  getUnreadCount(): Observable<number> {
    return this.http.get<number>(`${this.apiUrl}/unread-count`).pipe(
      tap((count) => this._unreadCount$.next(count)),
    );
  }

  markAsRead(id: number): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${id}/mark-read`, {}).pipe(
      tap(() => {
        const updated = this._notifications$.getValue().map((n) =>
          n.id === id ? { ...n, isRead: true, readAt: n.readAt ?? new Date().toISOString() } : n,
        );
        this._notifications$.next(updated);
        this._unreadCount$.next(Math.max(0, this._unreadCount$.getValue() - 1));
      }),
    );
  }

  markAllAsRead(): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/mark-all-read`, {}).pipe(
      tap(() => {
        const updated = this._notifications$.getValue().map((n) => ({
          ...n,
          isRead: true,
          readAt: n.readAt ?? new Date().toISOString(),
        }));
        this._notifications$.next(updated);
        this._unreadCount$.next(0);
      }),
    );
  }

  ngOnDestroy(): void {
    this.signalRService.stopConnection();
  }

}

